import React from 'react';

export type LineageCountsResult<TCounts> = {
  readonly countsLoading: boolean;
  readonly unfilteredCounts: TCounts | null;
  readonly filteredCounts: TCounts | null;
};

export type LineageCountsParentProps<TCounts> = {
  readonly children: (result: LineageCountsResult<TCounts>) => React.ReactNode;
};

export type LineageCountsCacherProps<TCounts> = LineageCountsParentProps<TCounts> & {
  readonly countsLoading: boolean;
  readonly newUnfilteredCounts?: TCounts | null;
  readonly newFilteredCounts?: TCounts | null;
};

type LineageCountsCacherState<TCounts> = {
  readonly unfilteredCounts: TCounts | null;
  readonly filteredCounts: TCounts | null;
};

/**
 * This component caches the values of two "counts" objects.
 * It also passes through a single "loading" boolean (without caching it).
 *
 * For either (or both) "counts" objects, a new value can be provided, or it can be left undefined.
 * Providing a new value will populate the cache and pass the new value to the children.
 * Leaving a value undefined will cause the cached value to be passed to the children.
 *
 * The cached value for each object is initially null.
 */
export class LineageCountsCacher<TCounts> extends React.Component<
  LineageCountsCacherProps<TCounts>,
  LineageCountsCacherState<TCounts>
> {

  public constructor(props: LineageCountsCacherProps<TCounts>) {
    super(props);
    this.state = {
      unfilteredCounts: props.newUnfilteredCounts || null,
      filteredCounts: props.newFilteredCounts || null,
    };
  }

  public static getDerivedStateFromProps<TCounts>(
    nextProps: LineageCountsCacherProps<TCounts>,
    prevState: LineageCountsCacherState<TCounts>
  ): LineageCountsCacherState<TCounts> {
    return {
      unfilteredCounts:
        nextProps.newUnfilteredCounts !== undefined ? nextProps.newUnfilteredCounts : prevState.unfilteredCounts,
      filteredCounts:
        nextProps.newFilteredCounts !== undefined ? nextProps.newFilteredCounts : prevState.filteredCounts,
    };
  }

  public override render(): React.ReactNode {
    return this.props.children({
      countsLoading: this.props.countsLoading,
      unfilteredCounts: this.state.unfilteredCounts,
      filteredCounts: this.state.filteredCounts,
    });
  }

}
